import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Loader2, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { ProjectSearchComboBox } from '@/components/shared/project-search-combo-box';
import type { ProjectOption } from '@/components/shared/project-search-combo-box';
import { useCreateComplianceIncident } from '@/hooks/use-compliance-incidents';
import { useOrgToday } from '@/hooks/use-settings';
import { PersonSearchComboBox, type PersonOption } from '@/components/shared/person-search-combo-box';
import type {
  ComplianceIncidentSeverity,
  ComplianceIncidentType,
  CreateComplianceIncidentPayload,
  PersonInvolved,
} from '@/lib/types';

interface PersonInvolvedRow extends Omit<PersonInvolved, 'person_id'> {
  tech_id: number | null;
  person_id: number | null;
}

const INCIDENT_TYPE_OPTIONS: { value: ComplianceIncidentType; label: string }[] = [
  { value: 'injury', label: 'Injury' },
  { value: 'near_miss_elevated', label: 'Elevated Near-Miss' },
  { value: 'property_damage', label: 'Property Damage' },
  { value: 'equipment_failure', label: 'Equipment Failure' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'regulatory_breach', label: 'Regulatory Breach' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_OPTIONS: { value: ComplianceIncidentSeverity; label: string }[] = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
  { value: 'critical', label: 'Critical' },
  { value: 'fatality', label: 'Fatality' },
];

const STEPS = ['Incident Details', 'People Involved', 'Immediate Actions'];

export function CreateComplianceIncidentPage() {
  const navigate = useNavigate();
  const createIncident = useCreateComplianceIncident();
  const orgToday = useOrgToday();
  const [step, setStep] = useState(0);

  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [form, setForm] = useState<CreateComplianceIncidentPayload>({
    title: '',
    date: orgToday,
    location: '',
    description: '',
    incident_type: 'other',
    severity: 'minor',
    immediate_actions_taken: '',
  });

  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [persons, setPersons] = useState<PersonInvolvedRow[]>([]);

  const updateField = <K extends keyof CreateComplianceIncidentPayload>(
    key: K,
    value: CreateComplianceIncidentPayload[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addPerson = () => {
    setPersons((prev) => [...prev, { name: '', role: '', injury_type: '', tech_id: null, person_id: null }]);
  };

  const updatePerson = (index: number, field: keyof PersonInvolved, value: string) => {
    setPersons((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    );
  };

  const updatePersonSelection = (index: number, person: PersonOption | null) => {
    setPersons((prev) =>
      prev.map((p, i) =>
        i === index
          ? {
              ...p,
              name: person?.name ?? '',
              tech_id: person?.tech_id ?? null,
              person_id: person?.person_id ?? null,
            }
          : p,
      ),
    );
  };

  const removePerson = (index: number) => {
    setPersons((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const payload: CreateComplianceIncidentPayload = {
      ...form,
      location_fk: selectedLocation?.id || null,
      project: selectedProject?.id,
      project_name: selectedProject?.name || form.project_name,
      project_number: selectedProject?.project_number || form.project_number,
      persons_involved: persons.length > 0
        ? persons.map((p) => ({
            name: p.name,
            role: p.role,
            injury_type: p.injury_type,
            ...(p.person_id ? { person_id: p.person_id } : {}),
          }))
        : undefined,
    };

    createIncident.mutate(payload, {
      onSuccess: (data) => {
        navigate(`/compliance/incidents/${data.id}`);
      },
    });
  };

  const canProceed = () => {
    if (step === 0) {
      return form.title && form.date && form.description;
    }
    return true;
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/compliance/incidents')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">Report Incident</h1>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                i === step
                  ? 'bg-primary text-primary-foreground'
                  : i < step
                    ? 'bg-green-100 text-green-800'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {i + 1}
            </div>
            <span className={`text-sm ${i === step ? 'font-medium' : 'text-muted-foreground'}`}>
              {label}
            </span>
            {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {/* Step 1: Incident Details */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Incident Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Brief description of the incident"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField('date', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={form.time || ''}
                  onChange={(e) => updateField('time', e.target.value || undefined)}
                />
              </div>
            </div>

            <div>
              <Label>Location</Label>
              <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
            </div>
            <div>
              <Label htmlFor="location">Specific Area / Details</Label>
              <Input
                id="location"
                value={form.location}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="e.g. East face, 3rd floor scaffolding"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="incident_type">Incident Type *</Label>
                <Select
                  value={form.incident_type}
                  onValueChange={(v) => updateField('incident_type', v as ComplianceIncidentType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INCIDENT_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="severity">Severity *</Label>
                <Select
                  value={form.severity}
                  onValueChange={(v) => updateField('severity', v as ComplianceIncidentSeverity)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Detailed account of what happened"
                rows={4}
              />
            </div>

            <div>
              <Label>Project</Label>
              <ProjectSearchComboBox
                value={selectedProject}
                onChange={(project) => {
                  setSelectedProject(project);
                  if (project?.location_fk && !selectedLocation) {
                    setSelectedLocation({ id: project.location_fk, name: project.location || '', type: project.location_fk_type || '' });
                  }
                }}
              />
            </div>

            <div>
              <Label htmlFor="injury_details">Injury Details</Label>
              <Textarea
                id="injury_details"
                value={form.injury_details || ''}
                onChange={(e) => updateField('injury_details', e.target.value || undefined)}
                placeholder="Description of injuries, if applicable"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: People Involved */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>People Involved</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {persons.map((person, index) => (
              <div key={index} className="flex items-start gap-4 rounded-md border p-4">
                <div className="flex-1 space-y-3">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <Label>Name</Label>
                      <PersonSearchComboBox
                        value={
                          person.name
                            ? { tech_id: person.tech_id, person_id: person.person_id, name: person.name }
                            : null
                        }
                        onChange={(selected) => updatePersonSelection(index, selected)}
                      />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Input
                        value={person.role}
                        onChange={(e) => updatePerson(index, 'role', e.target.value)}
                        placeholder="e.g., Rope Access Tech"
                      />
                    </div>
                    <div>
                      <Label>Injury Type</Label>
                      <Input
                        value={person.injury_type}
                        onChange={(e) => updatePerson(index, 'injury_type', e.target.value)}
                        placeholder="e.g., Laceration"
                      />
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removePerson(index)}
                  className="mt-6 text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            <Button variant="outline" onClick={addPerson}>
              <Plus className="mr-2 h-4 w-4" />
              Add Person
            </Button>

            {persons.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No people added yet. Click &quot;Add Person&quot; to record individuals involved in
                the incident.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Immediate Actions */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Immediate Actions Taken</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="immediate_actions_taken">
                Describe the immediate actions taken in response to the incident *
              </Label>
              <Textarea
                id="immediate_actions_taken"
                value={form.immediate_actions_taken}
                onChange={(e) => updateField('immediate_actions_taken', e.target.value)}
                placeholder="What actions were taken immediately after the incident?"
                rows={6}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => (step > 0 ? setStep(step - 1) : navigate('/compliance/incidents'))}>
          {step > 0 ? 'Previous' : 'Cancel'}
        </Button>

        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
            Next
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={createIncident.isPending}
          >
            {createIncident.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit Report
          </Button>
        )}
      </div>
    </div>
  );
}
